import type { Channel } from './channels';
import { COUNTRY_NAMES } from './channels';

function parseAttributes(extinfLine: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const attrRegex = /([\w-]+)="([^"]*)"/g;
  let match: RegExpExecArray | null;
  while ((match = attrRegex.exec(extinfLine)) !== null) {
    attrs[match[1]] = match[2];
  }
  return attrs;
}

function parseDisplayName(extinfLine: string): string {
  const commaIdx = extinfLine.lastIndexOf(',');
  if (commaIdx < 0) return '';
  return extinfLine.substring(commaIdx + 1).trim();
}

function extractCountryCode(tvgId: string | undefined): string {
  if (!tvgId) return 'XX';
  const match = tvgId.match(/\.([A-Za-z]{2})(?:@|$)/);
  if (match) return match[1].toUpperCase();
  return 'XX';
}

function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}

/**
 * Parse an iptv-org M3U text into Channel[]
 *
 * Handles the standard format:
 *   #EXTINF:-1 tvg-id="..." tvg-name="..." tvg-logo="..." group-title="...",Display Name
 *   https://stream.url/playlist.m3u8
 *
 * Deduplicates by tvg-id (first occurrence wins).
 */
export function parseM3U(text: string): Channel[] {
  const lines = text.split(/\r?\n/);
  const channels: Channel[] = [];
  const seenIds = new Set<string>();
  let pendingAttrs: Record<string, string> | null = null;
  let pendingName: string | null = null;

  for (const raw of lines) {
    const trimmed = raw.trim();
    if (!trimmed) continue;

    if (trimmed === '#EXTM3U') continue;

    if (trimmed.startsWith('#EXTINF:')) {
      pendingAttrs = parseAttributes(trimmed);
      pendingName = parseDisplayName(trimmed);
      continue;
    }

    if (trimmed.startsWith('#')) continue;

    if (pendingAttrs) {
      const tvgId = pendingAttrs['tvg-id'];
      let id = tvgId;
      if (!id) {
        id = `auto-${simpleHash(trimmed)}`;
      }

      if (!seenIds.has(id)) {
        seenIds.add(id);

        let countryCode = (pendingAttrs['tvg-country'] || '').toUpperCase();
        if (!countryCode) {
          countryCode = extractCountryCode(tvgId);
        }

        const country = COUNTRY_NAMES[countryCode] || countryCode;

        channels.push({
          id,
          name: pendingName || pendingAttrs['tvg-name'] || id,
          country,
          countryCode,
          category: pendingAttrs['group-title'] || 'General',
          logo: pendingAttrs['tvg-logo'] || '',
          tvgId: tvgId || undefined,
          url: trimmed,
        });
      }

      pendingAttrs = null;
      pendingName = null;
    }
  }

  return channels;
}
